import {
  Component,
  inject,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  ElementRef,
  Renderer2,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { trigger, state, style, transition, animate, AnimationEvent } from '@angular/animations';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { ComponentService } from '../../services/component.service';
import { ChatService } from '../../services/chat.service';
import { TenantComponents } from '../../types/Tenant';
import { ChatPanelComponent } from './chat-panel/chat-panel.component';

@Component({
  selector: 'app-chatbot',
  imports: [CommonModule, MatIconModule, MatButtonModule, TranslateModule, ChatPanelComponent],
  templateUrl: './chatbot.component.html',
  styleUrl: './chatbot.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('panelAnimation', [
      state('closed', style({ transform: 'translateY(100%)', opacity: 0, pointerEvents: 'none' })),
      state('open', style({ transform: 'translateY(0)', opacity: 1, pointerEvents: 'auto' })),
      transition('closed => open', animate('200ms ease-out')),
      transition('open => closed', animate('150ms ease-in')),
    ]),
  ],
})
export class ChatbotComponent implements OnInit, OnDestroy {
  private readonly componentService = inject(ComponentService);
  private readonly chatService = inject(ChatService);
  private readonly translateService = inject(TranslateService);
  private readonly renderer = inject(Renderer2);
  private readonly destroy$ = new Subject<void>();

  private readonly panelWrapper = viewChild<ElementRef<HTMLDivElement>>('panelWrapper');

  private resizeStart?: { x: number; y: number; width: number; height: number };
  private removeResizeListeners: Array<() => void> = [];
  private readonly minWidth = 320;
  private readonly minHeight = 360;
  private readonly mobileBreakpoint = 480;

  public isOpen = false;
  public showPanel = false;
  public isVisible = false;
  public openLabel = '';
  public closeLabel = '';

  public ngOnInit(): void {
    this.isVisible = this.componentService.isActive(TenantComponents.CHATBOT);
    if (this.isVisible) {
      this.chatService.refreshConversationTokenUsage();
    }
    this.updateLabels();
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateLabels();
    });
  }

  public ngOnDestroy(): void {
    this.clearResizeListeners();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public toggleChat(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.showPanel = true;
      this.chatService.refreshConversationTokenUsage();
    }
  }

  public onAnimationDone(event: AnimationEvent): void {
    if (event.toState === 'closed') {
      this.showPanel = false;
    }
  }

  public onResizeStart(event: MouseEvent | TouchEvent): void {
    const panelWrapper = this.panelWrapper();
    if (!panelWrapper || this.isMobile()) {
      return;
    }

    const point = this.getPoint(event);
    if (!point) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const wrapper = panelWrapper.nativeElement;
    const rect = wrapper.getBoundingClientRect();
    this.resizeStart = {
      x: point.x,
      y: point.y,
      width: rect.width,
      height: rect.height,
    };

    this.renderer.addClass(wrapper, 'is-resizing');
    this.bindResizeListeners();
  }

  private bindResizeListeners(): void {
    this.clearResizeListeners();
    const move = (event: MouseEvent | TouchEvent): void => this.onResizeMove(event);
    const end = (): void => this.onResizeEnd();

    this.removeResizeListeners = [
      this.renderer.listen('document', 'mousemove', move),
      this.renderer.listen('document', 'mouseup', end),
      this.renderer.listen('document', 'touchmove', move),
      this.renderer.listen('document', 'touchend', end),
      this.renderer.listen('document', 'touchcancel', end),
    ];
  }

  private onResizeMove(event: MouseEvent | TouchEvent): void {
    const panelWrapper = this.panelWrapper();
    if (!this.resizeStart || !panelWrapper) {
      return;
    }

    const point = this.getPoint(event);
    if (!point) {
      return;
    }

    const deltaX = point.x - this.resizeStart.x;
    const deltaY = point.y - this.resizeStart.y;
    const nextWidth = Math.max(this.minWidth, this.resizeStart.width - deltaX);
    const nextHeight = Math.max(this.minHeight, this.resizeStart.height - deltaY);

    const wrapper = panelWrapper.nativeElement;
    this.renderer.setStyle(wrapper, 'width', `${Math.round(nextWidth)}px`);
    this.renderer.setStyle(wrapper, 'height', `${Math.round(nextHeight)}px`);

    event.preventDefault();
  }

  private onResizeEnd(): void {
    const panelWrapper = this.panelWrapper();
    if (panelWrapper) {
      this.renderer.removeClass(panelWrapper.nativeElement, 'is-resizing');
    }

    this.resizeStart = undefined;
    this.clearResizeListeners();
  }

  private clearResizeListeners(): void {
    this.removeResizeListeners.forEach((remove) => remove());
    this.removeResizeListeners = [];
  }

  private getPoint(event: MouseEvent | TouchEvent): { x: number; y: number } | null {
    if (event instanceof MouseEvent) {
      return { x: event.clientX, y: event.clientY };
    }

    const touch = event.touches[0] ?? event.changedTouches[0];
    if (!touch) {
      return null;
    }

    return { x: touch.clientX, y: touch.clientY };
  }

  private isMobile(): boolean {
    return window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`).matches;
  }

  private updateLabels(): void {
    this.openLabel = this.translateService.instant('chatbot.open_chat');
    this.closeLabel = this.translateService.instant('chatbot.close_chat');
  }
}
